import { Chip, Stack } from "@mui/material";
import {
  IdentityUserStatus,
  type UserRoleGrantDto,
} from "Infrastructure/Api/Api";
import { isAccessRoleCode } from "Models/AccessRoleCodes";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  roleCode?: string | null;
  identityRoles: Array<UserRoleGrantDto>;
};

type RoleChip = {
  code: string;
  status: IdentityUserStatus;
  companyNames: Array<string>;
};

// A role can be held as the legacy access role or only as an Identity grant (self-added roles), so
// both sources are merged. One chip per role even when several grants share it (an identity can hold
// two merchant grants for two companies) — the companies are then listed in the tooltip, and an
// Active grant wins the displayed status so a live role never reads as pending.
const getRoleChips = (
  legacyRoleCode: string | null | undefined,
  identityRoles: Array<UserRoleGrantDto>,
): Array<RoleChip> => {
  const legacyCodes = legacyRoleCode ? [legacyRoleCode] : [];
  const grantCodes = identityRoles.map(({ accessRoleCode }) => accessRoleCode);
  const codes = Array.from(new Set([...legacyCodes, ...grantCodes]));

  return codes.map((code) => {
    const grants = identityRoles.filter(
      ({ accessRoleCode }) => accessRoleCode === code,
    );
    const status =
      code === legacyRoleCode ||
      grants.some(({ status }) => status === IdentityUserStatus.Active)
        ? IdentityUserStatus.Active
        : (grants.at(-1)?.status ?? IdentityUserStatus.Active);

    return {
      code,
      status,
      companyNames: grants
        .map((grant) => grant.partyCompanyName)
        .filter(
          (name, index, names): name is string =>
            !!name && names.indexOf(name) === index,
        ),
    };
  });
};

export const UserRolesCell = ({ roleCode, identityRoles }: Props) => {
  const { t } = useResource();

  const chips = getRoleChips(roleCode, identityRoles);

  if (chips.length === 0) {
    return <>-</>;
  }

  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      {chips.map(({ code, status, companyNames }) => {
        const isActive = status === IdentityUserStatus.Active;
        const roleLabel = isAccessRoleCode(code)
          ? t(Resources.User.AccessRole[code])
          : code;

        return (
          <Chip
            key={code}
            size="small"
            variant="outlined"
            color={isActive ? "default" : "warning"}
            title={companyNames.join(", ") || undefined}
            label={
              isActive
                ? roleLabel
                : `${roleLabel} (${t(Resources.Form.IdentityUserStatus[status])})`
            }
          />
        );
      })}
    </Stack>
  );
};
